import { TRANSACTION_TYPE } from '@dealstack/shared/lib/common/business/investor';
import {
	GetInvestorTransactionDetailsRes,
	LeaverInvestmentWorkflowType,
	LoanDefinitionType,
} from '@dealstack/shared/lib/generated';
import type {
	TransactionTableVestingTerm,
	TransactionTableView,
	TransactionVestingTermAccess,
} from './transactionTable.types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const isSellTransactionType = (transactionType?: string, isNegative?: boolean) => {
	if (isNegative) {
		return false;
	}
	if (!transactionType) {
		return true;
	}
	return transactionType === TRANSACTION_TYPE.SELL;
};

export const getTransactionTypeLabel = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>,
	transactionType?: string
): string | undefined =>
	transactionType
		? tMipManage(`investors.expanded_row.${transactionType.toLowerCase()}`)
		: undefined;

export const prepareSignedTotalValue = (row: GetInvestorTransactionDetailsRes): number => {
	if (
		row.loanType === LoanDefinitionType.MANAGEMENT_LOAN ||
		row.loanType === LoanDefinitionType.DEFERRED_PAYMENT
	) {
		return row.isNegative ? row.totalValue : -row.totalValue;
	}
	return row.isNegative || row.transactionType === TRANSACTION_TYPE.SELL
		? -row.totalValue
		: row.totalValue;
};

export const isLeaverTransaction = (transaction: GetInvestorTransactionDetailsRes): boolean =>
	transaction.investmentWorkflowComputedType === LeaverInvestmentWorkflowType.LEAVER ||
	transaction.investmentWorkflowComputedType === LeaverInvestmentWorkflowType.PARTIAL_LEAVER;

export const getTransactionVestingTermAccess = ({
	transaction,
	hasVestingTerms,
	view,
}: {
	transaction: GetInvestorTransactionDetailsRes;
	hasVestingTerms: boolean;
	view: TransactionTableView;
}): TransactionVestingTermAccess | null => {
	if (
		view !== 'completed' ||
		isLeaverTransaction(transaction) ||
		!transaction.isSubjectToVesting
	) {
		return null;
	}

	if (transaction.isIssue) {
		return hasVestingTerms ? 'edit' : null;
	}

	return transaction.vestingTermId ? 'remove' : null;
};

/** A term moved in from another programme is missing from this one's list, so fall back to its own name. */
export const getVestingTermName = (
	vestingTerms: TransactionTableVestingTerm[] | undefined,
	transaction: GetInvestorTransactionDetailsRes
): string | undefined =>
	vestingTerms?.find(({ id }) => id === transaction.vestingTermId)?.name ??
	transaction.vestingTermName;
